import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearToken } from "@/lib/api";
import { LogOut, FileText, Upload, Menu, X, Copy, BookOpen, Users, BarChart3, UserCog } from "lucide-react";

const AdminShell = ({ children, title, actions }) => {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const item = (to, icon, label, testid) => {
    const active = pathname === to || (to !== "/admin" && pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`flex items-center gap-3 px-5 py-3 font-sans text-xs tracking-[0.25em] uppercase transition-colors ${
          active ? "bg-[#1a1918] text-[#f9f8f6]" : "text-[#1a1918] hover:bg-[#e5e1d8]"
        }`}
      >
        {icon}
        {label}
      </Link>
    );
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  const SidebarContents = (
    <>
      <div className="px-5 py-6 sm:py-8 border-b border-[#e5e1d8] flex items-center justify-between">
        <div>
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Studio</p>
          <Link to="/admin" className="font-serif text-2xl tracking-tight text-[#1a1918]">
            Attraversando
          </Link>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="admin-drawer-close"
          className="lg:hidden p-2 -mr-2 text-[#1a1918]"
          aria-label="chiudi menu"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 py-4 flex flex-col gap-1">
        {item("/admin", <FileText size={14} />, "Post", "nav-admin-posts")}
        {item("/admin/import", <Upload size={14} />, "Importa RTF", "nav-admin-import")}
        {item("/admin/duplicati", <Copy size={14} />, "Duplicati", "nav-admin-duplicates")}
        {item("/admin/autori", <UserCog size={14} />, "Autori", "nav-admin-authors")}
        {item("/admin/lettori", <Users size={14} />, "Lettori", "nav-admin-users")}
        {item("/admin/statistiche", <BarChart3 size={14} />, "Statistiche", "nav-admin-stats")}
        {item("/admin/libro", <BookOpen size={14} />, "Libro PDF", "nav-admin-export")}
      </nav>
      <button
        data-testid="admin-logout"
        onClick={logout}
        className="m-5 flex items-center gap-3 border border-[#1a1918] py-3 px-4 font-sans text-xs tracking-[0.25em] uppercase hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
      >
        <LogOut size={14} />
        esci
      </button>
      <Link
        to="/"
        data-testid="admin-view-public"
        className="mx-5 mb-5 text-center font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] hover:text-[#1a1918]"
      >
        ↗ vedi blog
      </Link>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f9f8f6]" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#f2efe9] border-r border-[#e5e1d8] flex-col sticky top-0 h-screen">
        {SidebarContents}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          data-testid="admin-drawer"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-[#f2efe9] flex flex-col shadow-xl"
            style={{ animation: "slideIn .22s ease-out" }}
          >
            {SidebarContents}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#e5e1d8] bg-[#f9f8f6] sticky top-0 z-20">
          <button
            onClick={() => setOpen(true)}
            data-testid="admin-drawer-open"
            className="p-2 -ml-2 text-[#1a1918]"
            aria-label="apri menu"
          >
            <Menu size={22} />
          </button>
          <Link to="/admin" className="font-serif text-lg tracking-tight text-[#1a1918]">
            Attraversando
          </Link>
          <Link
            to="/"
            data-testid="admin-mobile-public"
            className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843]"
          >
            blog ↗
          </Link>
        </div>

        <header className="border-b border-[#e5e1d8] px-4 sm:px-8 lg:px-10 py-5 lg:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 bg-[#f9f8f6]">
          <h1 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#1a1918] font-light">{title}</h1>
          {actions && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="admin-actions">
              {actions}
            </div>
          )}
        </header>

        <div className="flex-1 px-4 sm:px-8 lg:px-10 py-6 lg:py-8 overflow-x-hidden">{children}</div>
      </main>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default AdminShell;
